// Tester feedback (D-LAUNCH3): the vocabulary and the validation both bundles
// share. The iPad draws its chips from these lists and the worker refuses
// anything outside them, so a category can never exist on one side only.
// This file also holds normalizeRoute, the route-folding helper the
// app_errors per-route cap uses to bucket by endpoint shape, not by id.
// Pure — no DOM, no D1 — so node --test covers it directly.

export const REPORT_CATEGORIES = [
  { id: 'broke', label: 'Something broke' },
  { id: 'confusing', label: 'Confusing' },
  { id: 'wrong_info', label: 'Wrong info' },
  { id: 'idea', label: 'Idea' },
];

// Why a tech thumbed an AI reply down.
export const RATING_REASONS = [
  { id: 'wrong', label: 'Wrong' },
  { id: 'unsafe', label: 'Unsafe' },
  { id: 'confusing', label: 'Confusing' },
  { id: 'too_long', label: 'Too long' },
];

export const MAX_MESSAGE_CHARS = 4000;
export const MAX_RECENT_ERRORS = 5;

const REPORT_IDS = new Set(REPORT_CATEGORIES.map((c) => c.id));
const REASON_IDS = new Set(RATING_REASONS.map((r) => r.id));

/**
 * Trimmed string capped at `max`, or null when blank/absent. Only strings and
 * numbers are coerced — a hostile JSON body can carry an object like
 * `{ toString: 1 }` in any field, and `String(value)` on that throws
 * ("Cannot convert object to primitive value"), which would 500 the route
 * instead of rejecting the field.
 */
export function clip(value, max) {
  if (value == null) return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const s = String(value).trim();
  return s ? s.slice(0, max) : null;
}

// Numbers must be safe integers (rules out true/false, which Number() below
// would coerce to 1/0, and rules out magnitudes like 1e20 that lose
// precision). Strings must be plain decimal digits — /^\d+$/ before the
// Number() call, so hex ('0x10') and exponent ('1e3') notation never sneak a
// number in through a string field.
function positiveInt(value) {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    if (!/^\d+$/.test(s)) return null;
    const n = Number(s);
    return Number.isSafeInteger(n) && n > 0 ? n : null;
  }
  return null;
}

// The iPad's ring buffer (src/utils/recentErrors.js) is client-supplied, so
// every field is re-clipped here and anything not on the list is dropped.
function cleanRecentErrors(list) {
  if (!Array.isArray(list)) return [];
  return list
    .slice(-MAX_RECENT_ERRORS)
    .filter((e) => e && typeof e === 'object')
    .map((e) => ({
      at: clip(e.at, 40),
      method: clip(e.method, 10),
      path: clip(e.path, 200),
      status: Number.isInteger(e.status) ? e.status : null,
      message: clip(e.message, 200),
    }));
}

/**
 * Validate a Feedback-button report. `hasPhoto` lets a photo stand in for
 * words (a screenshot often says it all). Returns { ok, value } or
 * { ok: false, error } with a message fit to show the tech.
 */
export function validateReport(input, { hasPhoto = false } = {}) {
  const b = input && typeof input === 'object' ? input : {};
  if (!REPORT_IDS.has(b.category)) return { ok: false, error: 'Pick what kind of feedback this is.' };
  const message = clip(b.message, MAX_MESSAGE_CHARS);
  if (!message && !hasPhoto) return { ok: false, error: 'Say what happened, or attach a photo.' };
  return {
    ok: true,
    value: {
      category: b.category,
      message,
      route: clip(b.route, 200),
      model_number: clip(b.model_number, 64),
      lookup_id: positiveInt(b.lookup_id),
      session_id: positiveInt(b.session_id),
      recent_errors: JSON.stringify(cleanRecentErrors(b.recent_errors)),
    },
  };
}

/**
 * Validate a 👍/👎 on an AI turn. rating 0 means "clear my rating". A reason
 * and note only ride on a thumbs-down; an unknown reason is dropped rather
 * than refused, because the thumb itself is the signal that matters.
 */
export function validateRating(input) {
  const b = input && typeof input === 'object' ? input : {};
  const turn_id = positiveInt(b.turn_id);
  if (!turn_id) return { ok: false, error: 'turn_id is required.' };
  // Strict membership, not Number(b.rating): the caller sends real JSON
  // numbers, and Number() coerces null/''/[] to 0 (a malformed body would
  // silently read as the destructive "clear my rating") and true to 1.
  const rating = b.rating;
  if (![1, -1, 0].includes(rating)) return { ok: false, error: 'rating must be 1, -1 or 0.' };
  const down = rating === -1;
  return {
    ok: true,
    value: {
      turn_id,
      rating,
      category: down && REASON_IDS.has(b.category) ? b.category : null,
      message: down ? clip(b.message, MAX_MESSAGE_CHARS) : null,
    },
  };
}

/**
 * Who a rating belongs to, for the one-rating-per-tech-per-turn upsert. The
 * email when signed in; the typed name on the NameGate fallback, lowercased
 * so 'Jason' and 'jason' are one rater. (A NULL email can't be the key:
 * SQLite treats NULLs as distinct in a unique index.)
 */
export function raterKey({ email, name } = {}) {
  const e = clip(email, 200);
  if (e) return e.toLowerCase();
  const n = clip(name, 100);
  return n ? `name:${n.toLowerCase()}` : null;
}

// The SPA's real routes (src/App.jsx). A client crash report picks its own
// `route` string (ErrorBoundary sends window.location.pathname), and without
// mapping it to one of these before it reaches logAppError's per-(route,
// source) cap, a hostile or buggy client could mint a fresh route on every
// request and log an unbounded number of client errors — see clientRoute.
export const CLIENT_ROUTES = ['/', '/result', '/troubleshoot', '/wiki', '/history', '/help', '/admin'];

/**
 * Map a client-supplied SPA path to one of `CLIENT_ROUTES`, or '/other' for
 * anything else. Query string and a trailing slash are stripped first so
 * '/result?x=1' and '/result/' both match '/result'. Non-string input (a
 * hostile JSON body) is treated as empty rather than passed to String(),
 * which can throw.
 */
export function clientRoute(raw) {
  const stripped = (typeof raw === 'string' ? raw : '').split('?')[0].replace(/\/+$/, '');
  const path = stripped || '/';
  return CLIENT_ROUTES.includes(path) ? path : '/other';
}

/**
 * '/api/troubleshoot/123/turn' → '/api/troubleshoot/:id/turn', query dropped.
 * Folds any path segment that CONTAINS a digit, not just an all-digit one —
 * '/api/documents/RF29BB' → '/api/documents/:id' — because the app_errors
 * cap keys on the folded route: without this, every model number would get
 * its own 50/hour bucket instead of sharing the route's. Every literal route
 * keyword in worker/index.js is alphabetic, so this can't fold a real route
 * segment by mistake. Non-string input (a hostile JSON body) is treated as
 * empty rather than passed to String(), which can throw.
 */
export function normalizeRoute(path) {
  const p = (typeof path === 'string' ? path : '').split('?')[0];
  return p
    .split('/')
    .map((seg) => (/\d/.test(seg) ? ':id' : seg))
    .join('/')
    .slice(0, 200) || '/';
}
